import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, Legend, LineChart, Line,
} from 'recharts';
import { apiFetch } from '../lib/apiClient';
import { useCustomers } from '../context/CustomerContext';
import { reportCacheConfig } from '../lib/reportCache';
import { ReportFooter } from './reports/ReportView';
import { exportDomAsHtml, exportDomAsPdf, slugify } from '../lib/exportDomReport';

interface MonthRow {
  month: string;
  hasSnapshot: boolean;
  isPast: boolean;
  totalIncidents: number;
  severityTotal: number;
  highSeverityIncidents: number;
  source: 'auto' | 'manual' | 'backfill' | null;
  reconciled: 'yes' | 'no' | 'unknown';
}

interface TwelveMonthOverviewItem {
  month: string;
  hasData: boolean;
  alerts: number;
  incidents: number;
  tpr: number | null;
}

interface Aggregates {
  threatsNeutralised: {
    totalInvestigated: number;
    realAttacks: number;
    benignResolved: number;
    falsePositives: number;
    avgMttrMinutes: number | null;
  };
  topAttacks: { title: string; totalFires: number; monthsActive: number; peakMonth: string; peakCount: number }[];
  slaTrend: { month: string; avgMttdMinutes: number | null; avgMttrMinutes: number | null }[];
  slaImprovement: {
    mttdPct: number | null; mttrPct: number | null;
    mttdFirstMonth: string | null; mttdLastMonth: string | null;
    mttrFirstMonth: string | null; mttrLastMonth: string | null;
  };
  fpRatioTrend: { month: string; fp: number; classifiedTotal: number; ratioPct: number }[];
  fpImprovement: { pct: number | null; firstMonth: string | null; lastMonth: string | null };
  mitreCoverage: { tactic: string; count: number; monthsActive: number }[];
  insiderYear: {
    flaggedUserCount: number;
    highRiskUserCount: number;
    topRiskyUsers: { user: string; maxScore: number; monthsFlagged: number; topSignal: string; level: string }[];
  };
  identitySurface: {
    failedSignInTotal: number;
    topCountries: { country: string; count: number }[];
    topFailureReasons: { description: string; count: number }[];
  };
  threatIntelYear: {
    totalMatches: number;
    byType: { indicatorType: string; count: number }[];
    byMonth: { month: string; count: number }[];
  };
  assetRisk: {
    totalEvents: number;
    topAssets: { host: string; count: number; sharePct: number }[];
    top5SharePct: number;
  };
}

interface YearlyReportData {
  customerId: string;
  year: number;
  timezone: string;
  generatedAt: string;
  yearTotals: {
    totalIncidents: number;
    severityTotal: number;
    highSeverityIncidents: number;
    monthsCovered: number;
  };
  missingMonths: string[];
  months: MonthRow[];
  aggregates: Aggregates;
  twelveMonthOverview: TwelveMonthOverviewItem[];
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const monthLabel = (yyyyMm: string): string => {
  const m = Number(yyyyMm.slice(5, 7));
  return MONTH_NAMES[m - 1] ?? yyyyMm;
};

const fmtInt = (n: number): string => Math.round(n).toLocaleString();
const fmtMins = (n: number | null | undefined): string =>
  n == null || !Number.isFinite(n) ? ' - ' : `${n.toFixed(1)} min`;
const fmtPct = (n: number | null | undefined, digits = 1): string =>
  n == null || !Number.isFinite(n) ? ' - ' : `${n.toFixed(digits)}%`;

/** Render a "vs first month" delta where NEGATIVE = improvement (lower MTTR/MTTD/FP is better). */
function deltaPill(pct: number | null): { text: string; color: string; tone: 'good' | 'bad' | 'neutral' } {
  if (pct == null || !Number.isFinite(pct)) return { text: ' - ', color: '#64748b', tone: 'neutral' };
  if (Math.abs(pct) < 0.5) return { text: 'flat', color: '#64748b', tone: 'neutral' };
  const tone: 'good' | 'bad' = pct < 0 ? 'good' : 'bad';
  const color = tone === 'good' ? '#16a34a' : '#b91c1c';
  const arrow = tone === 'good' ? '↓' : '↑';
  return { text: `${arrow} ${Math.abs(pct).toFixed(1)}%`, color, tone };
}

/**
 * Yearly SOC Report — the year-end "wow" report for a customer's exec/board.
 *
 * Tier 1: headline (threats neutralised, top attacks)
 * Tier 2: improvement story (MTTD/MTTR, false-positive ratio, MITRE coverage)
 * Tier 3: proactive value (insider, identity surface, threat intel, assets)
 *
 * All aggregations are computed server-side from the 12 monthly snapshots in
 * `monthly_report_snapshots`; this component only renders.
 */
export function YearlyReportTab() {
  const { activeCustomerId, activeCustomer, customers } = useCustomers();
  const customerId = activeCustomerId;
  const customer = activeCustomer;
  const customerName = customer?.name ?? customerId ?? 'Customer';

  const currentYear = new Date().getUTCFullYear();
  const [year, setYear] = useState<number>(currentYear);
  const [backfilling, setBackfilling] = useState<string | null>(null);
  const [backfillError, setBackfillError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const yearStr = String(year);
  const customerTz = customer?.timezone ?? 'UTC';
  const yearlyQuery = useQuery<YearlyReportData, Error>({
    queryKey: ['yearly-report', customerId, year],
    queryFn: () => apiFetch<YearlyReportData>(
      `/yearly-report?customerId=${encodeURIComponent(customerId ?? '')}&year=${year}`,
    ),
    enabled: !!customerId && customerId !== 'default',
    ...reportCacheConfig('yearly', yearStr, customerTz),
  });
  const data = yearlyQuery.data ?? null;
  const loading = yearlyQuery.isLoading || yearlyQuery.isFetching;
  const queryError = yearlyQuery.error?.message ?? null;
  const error = (!customerId || customerId === 'default')
    ? 'Pick a specific customer to view their yearly report.'
    : backfillError ?? queryError;

  const backfill = useCallback(async (m: string) => {
    if (!customerId) return;
    setBackfilling(m);
    setBackfillError(null);
    try {
      await apiFetch<unknown>(`/monthly-report/snapshot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId, month: m, source: 'manual' }),
      });
      void queryClient.invalidateQueries({ queryKey: ['yearly-report', customerId, year] });
      void queryClient.invalidateQueries({ queryKey: ['monthly-report', customerId, m] });
    } catch (e) {
      setBackfillError(e instanceof Error ? `Backfill ${m} failed: ${e.message}` : 'Backfill failed.');
    } finally {
      setBackfilling(null);
    }
  }, [customerId, year, queryClient]);

  // Both HTML and PDF clone the on-screen report DOM (with all page CSS
  // inlined) rather than redrawing it, so the export always matches
  // whatever the customer actually sees.
  const [exportState, setExportState] = useState<
    | { kind: 'idle' }
    | { kind: 'busy'; which: 'html' | 'pdf' }
    | { kind: 'error'; which: 'html' | 'pdf'; message: string }
  >({ kind: 'idle' });

  const exportOpts = useCallback(() => {
    const exportDateStr = new Date().toISOString().slice(0, 10);
    return {
      rootSelector: '.daily-report-root',
      title: `${customerName} - Yearly SOC Report - ${year}`,
      fileNameBase: `kloudynet-yearly-soc-report-${slugify(customerName)}-${year}-${exportDateStr}`,
      notMountedError: 'Yearly report content not visible - load a report first.',
    };
  }, [customerName, year]);

  const runExport = useCallback(async (which: 'html' | 'pdf') => {
    setExportState({ kind: 'busy', which });
    const result = which === 'html' ? await exportDomAsHtml(exportOpts()) : await exportDomAsPdf(exportOpts());
    if (result.ok) {
      setExportState({ kind: 'idle' });
    } else {
      setExportState({ kind: 'error', which, message: result.error ?? 'Export failed.' });
      window.setTimeout(() => setExportState({ kind: 'idle' }), 6000);
    }
  }, [exportOpts]);

  const chartData = useMemo(() => {
    if (!data) return [];
    return data.months.map((m, i) => ({
      label: MONTH_NAMES[i] ?? m.month,
      total: m.hasSnapshot ? m.totalIncidents : 0,
      severity: m.hasSnapshot ? m.severityTotal : 0,
      high: m.hasSnapshot ? m.highSeverityIncidents : 0,
    }));
  }, [data]);

  const slaChartData = useMemo(() => {
    const a = data?.aggregates;
    if (!a) return [];
    return a.slaTrend.map((r) => ({
      label: monthLabel(r.month),
      mttd: r.avgMttdMinutes,
      mttr: r.avgMttrMinutes,
    }));
  }, [data]);

  const fpChartData = useMemo(() => {
    const a = data?.aggregates;
    if (!a) return [];
    return a.fpRatioTrend.map((r) => ({
      label: monthLabel(r.month),
      ratioPct: Number(r.ratioPct.toFixed(2)),
    }));
  }, [data]);

  const twelveMonthChartData = useMemo(() => {
    if (!data?.twelveMonthOverview) return [];
    return data.twelveMonthOverview.map((m) => ({
      label: `${monthLabel(m.month)} ${m.month.slice(0, 4)}`,
      alerts: m.hasData ? m.alerts : 0,
      incidents: m.hasData ? m.incidents : 0,
      tpr: m.hasData && m.tpr != null ? Number(m.tpr.toFixed(1)) : null,
    }));
  }, [data]);

  const yearOptions = useMemo(() => {
    const out: number[] = [];
    for (let y = currentYear; y >= currentYear - 4; y--) out.push(y);
    return out;
  }, [currentYear]);

  const customerOptions = customers.filter((c) => c.id !== 'default' && c.enabled !== false);

  if (!customerId || customerId === 'default') {
    return (
      <div className="rounded-2xl bg-white p-8 text-slate-700 shadow-md">
        <h2 className="text-lg font-semibold text-slate-900 mb-2">Yearly SOC Report</h2>
        <p className="text-sm">
          Select a specific customer from the dropdown above. The yearly view aggregates
          monthly snapshots; the MSSP "default" workspace doesn't have any.
        </p>
        {customerOptions.length === 0 && (
          <p className="text-sm mt-3 text-slate-500">No enabled customers configured.</p>
        )}
      </div>
    );
  }

  const a = data?.aggregates;

  return (
    <div className="daily-report-root">
      <style>{`
        .yr-section { background: #fff; border-radius: 14px; box-shadow: 0 4px 18px rgba(0,0,0,0.08); margin-bottom: 22px; overflow: hidden; }
        .yr-section h3 { margin: 0; padding: 14px 22px; background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%); color: #fff; font-size: 1em; font-weight: 600; letter-spacing: 0.3px; }
        .yr-body { padding: 20px 22px; }
        .yr-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 14px; }
        .yr-kpi { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 14px 16px; }
        .yr-kpi-value { font-size: 1.8em; font-weight: 700; color: #1e3c72; line-height: 1.1; }
        .yr-kpi-label { font-size: 0.78em; color: #64748b; margin-top: 4px; }
        .yr-table { width: 100%; border-collapse: collapse; font-size: 13px; }
        .yr-table th { text-align: left; padding: 9px 10px; background: #f1f5f9; color: #1e3c72; font-weight: 600; border-bottom: 2px solid #e2e8f0; }
        .yr-table td { padding: 8px 10px; border-bottom: 1px solid #f1f5f9; color: #1f2937; }
        .yr-pill { display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 11px; font-weight: 700; letter-spacing: 0.3px; }
        .yr-pill.auto { background: #dcfce7; color: #166534; }
        .yr-pill.manual { background: #dbeafe; color: #1e40af; }
        .yr-pill.backfill { background: #fef3c7; color: #92400e; }
        .yr-pill.missing { background: #fee2e2; color: #991b1b; }
        .yr-pill.future { background: #e2e8f0; color: #475569; }
        .yr-pill.high { background: #fee2e2; color: #991b1b; }
        .yr-pill.medium { background: #fef3c7; color: #92400e; }
        .yr-pill.low { background: #dbeafe; color: #1e40af; }

        /* Hero "threats neutralised" band */
        .yr-hero { background: linear-gradient(135deg, #0f172a 0%, #1e3c72 50%, #2a5298 100%); color: #fff; padding: 26px 28px; border-radius: 14px; margin-bottom: 22px; box-shadow: 0 6px 24px rgba(15,23,42,0.18); }
        .yr-hero-headline { font-size: 1.05em; line-height: 1.55; }
        .yr-hero-headline strong { color: #fcd34d; font-size: 1.15em; }
        .yr-hero-stats { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 16px; margin-top: 18px; }
        .yr-hero-stat { background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.15); border-radius: 10px; padding: 14px 16px; }
        .yr-hero-stat .v { font-size: 1.7em; font-weight: 700; line-height: 1.05; }
        .yr-hero-stat .l { font-size: 0.74em; opacity: 0.78; margin-top: 4px; letter-spacing: 0.4px; text-transform: uppercase; }

        /* Two-column layouts */
        .yr-2col { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; }
        @media (max-width: 900px) { .yr-2col { grid-template-columns: 1fr; } }

        /* MITRE bar */
        .yr-mitre-row { display: grid; grid-template-columns: 160px 1fr 70px; align-items: center; gap: 10px; padding: 6px 0; border-bottom: 1px dashed #e2e8f0; font-size: 13px; }
        .yr-mitre-row:last-child { border-bottom: none; }
        .yr-mitre-bar { background: #e2e8f0; border-radius: 4px; height: 16px; overflow: hidden; }
        .yr-mitre-bar > div { height: 100%; background: linear-gradient(90deg, #1e3c72, #2a5298); border-radius: 4px; }

        /* Improvement chip */
        .yr-improvement { display: inline-flex; align-items: center; gap: 6px; padding: 4px 10px; border-radius: 999px; font-size: 0.78em; font-weight: 700; letter-spacing: 0.3px; }
      `}</style>

      <div className="dr-no-print mb-4 flex flex-wrap items-end gap-3 rounded-xl bg-white/70 dark:bg-slate-800/70 backdrop-blur px-4 py-3 shadow-sm border border-white dark:border-slate-700">
        <div>
          <h2 className="text-base font-semibold text-slate-800 dark:text-slate-100">Yearly SOC Report</h2>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Customer: <span className="text-slate-700 dark:text-slate-200 font-medium">{customerName}</span>
          </p>
        </div>
        <div className="ml-auto flex items-end gap-3">
          <label className="text-xs text-slate-600 dark:text-slate-300">
            <span className="block mb-1">Year</span>
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-1.5 text-sm text-slate-800 dark:text-slate-100 dark:[color-scheme:dark]"
            >
              {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </label>
          <button
            type="button"
            onClick={() => { void yearlyQuery.refetch(); }}
            disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs font-semibold text-slate-700 dark:text-slate-200 shadow-sm hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50"
          >
            {loading ? 'Loading…' : 'Refresh'}
          </button>
          {data && (
            <button
              type="button"
              onClick={() => { void runExport('html'); }}
              disabled={exportState.kind === 'busy'}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs font-semibold text-slate-700 dark:text-slate-200 shadow-sm hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50"
            >
              {exportState.kind === 'busy' && exportState.which === 'html' ? 'Exporting…' : '⬇ Download HTML'}
            </button>
          )}
          {data && (
            <button
              type="button"
              onClick={() => { void runExport('pdf'); }}
              disabled={exportState.kind === 'busy'}
              className="inline-flex items-center gap-1.5 rounded-lg bg-blue-700 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-blue-800 disabled:opacity-50"
            >
              {exportState.kind === 'busy' && exportState.which === 'pdf' ? 'Exporting…' : '⬇ Download PDF'}
            </button>
          )}
        </div>
      </div>

      {exportState.kind === 'error' && (
        <div className="dr-no-print mb-3 rounded-xl border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/40 p-3 text-sm text-red-800 dark:text-red-300">{exportState.message}</div>
      )}

      {error && (
        <div className="mb-3 rounded-xl border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/40 p-3 text-sm text-red-800 dark:text-red-300">{error}</div>
      )}

      {loading && !data && (
        <div className="rounded-2xl bg-white dark:bg-slate-800 p-8 text-center text-slate-600 dark:text-slate-300 shadow">
          <div className="text-sm">Loading yearly snapshot data…</div>
        </div>
      )}

      {data && a && (
        <>
          {/* ── Tier 1.A — Threats Neutralised hero ── */}
          <div className="yr-hero">
            <div className="yr-hero-headline">
              In {year}, we investigated{' '}
              <strong>{fmtInt(a.threatsNeutralised.totalInvestigated)}</strong>{' '}
              security incidents for <strong>{customerName}</strong>.{' '}
              <strong>{fmtInt(a.threatsNeutralised.realAttacks)}</strong> were
              confirmed real attacks; the rest were triaged as benign or false alarms.
              {a.threatsNeutralised.avgMttrMinutes != null && (
                <> Average time to resolve: <strong>{fmtMins(a.threatsNeutralised.avgMttrMinutes)}</strong>.</>
              )}
            </div>
            <div className="yr-hero-stats">
              <div className="yr-hero-stat">
                <div className="v">{fmtInt(a.threatsNeutralised.totalInvestigated)}</div>
                <div className="l">Incidents Investigated</div>
              </div>
              <div className="yr-hero-stat">
                <div className="v" style={{ color: '#fca5a5' }}>{fmtInt(a.threatsNeutralised.realAttacks)}</div>
                <div className="l">Confirmed Real Attacks</div>
              </div>
              <div className="yr-hero-stat">
                <div className="v">{fmtInt(a.threatsNeutralised.benignResolved)}</div>
                <div className="l">Benign / Resolved</div>
              </div>
              <div className="yr-hero-stat">
                <div className="v">{fmtMins(a.threatsNeutralised.avgMttrMinutes)}</div>
                <div className="l">Avg Time to Resolve</div>
              </div>
            </div>
          </div>

          {/* ── Year totals (existing) ── */}
          <div className="yr-section">
            <h3>{year} year totals · {customerName}</h3>
            <div className="yr-body">
              <div className="yr-kpis">
                <div className="yr-kpi">
                  <div className="yr-kpi-value">{data.yearTotals.totalIncidents.toLocaleString()}</div>
                  <div className="yr-kpi-label">Classified incidents (sum)</div>
                </div>
                <div className="yr-kpi">
                  <div className="yr-kpi-value">{data.yearTotals.severityTotal.toLocaleString()}</div>
                  <div className="yr-kpi-label">Total incidents (severity sum)</div>
                </div>
                <div className="yr-kpi">
                  <div className="yr-kpi-value" style={{ color: '#b91c1c' }}>
                    {data.yearTotals.highSeverityIncidents.toLocaleString()}
                  </div>
                  <div className="yr-kpi-label">High-severity incidents</div>
                </div>
                <div className="yr-kpi">
                  <div className="yr-kpi-value">{data.yearTotals.monthsCovered} / 12</div>
                  <div className="yr-kpi-label">Months with a snapshot</div>
                </div>
              </div>
              {data.missingMonths.length > 0 && (
                <p className="mt-4 text-xs text-amber-700">
                  Past months missing a snapshot: {data.missingMonths.join(', ')} - back-fill below
                  or open the Monthly tab and click <em>Save snapshot</em>.
                </p>
              )}
            </div>
          </div>

          {/* ── Monthly trend (existing) ── */}
          <div className="yr-section">
            <h3>Monthly trend</h3>
            <div className="yr-body" style={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                  <RTooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="severity" name="Total" fill="#2a5298" />
                  <Bar dataKey="high" name="High" fill="#dc2626" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* ── Tier 1.B — Top attacks of the year ── */}
          <div className="yr-section">
            <h3>Top attacks we handled this year</h3>
            <div className="yr-body" style={{ overflowX: 'auto' }}>
              {a.topAttacks.length === 0 ? (
                <p className="text-sm text-slate-500 italic">No incident data available.</p>
              ) : (
                <table className="yr-table">
                  <thead>
                    <tr>
                      <th style={{ width: 30 }}>#</th>
                      <th>Detection</th>
                      <th style={{ textAlign: 'right' }}>Total fires</th>
                      <th style={{ textAlign: 'right' }}>Months active</th>
                      <th>Peak month</th>
                      <th style={{ textAlign: 'right' }}>Peak count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {a.topAttacks.map((t, i) => (
                      <tr key={t.title}>
                        <td style={{ color: '#94a3b8' }}>{i + 1}</td>
                        <td style={{ fontWeight: 500 }}>{t.title}</td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>{t.totalFires.toLocaleString()}</td>
                        <td style={{ textAlign: 'right' }}>{t.monthsActive}/12</td>
                        <td>{monthLabel(t.peakMonth)} {t.peakMonth.slice(0, 4)}</td>
                        <td style={{ textAlign: 'right' }}>{t.peakCount.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* ── Tier 2.A — SLA improvement ── */}
          <div className="yr-section">
            <h3>Detection &amp; response speed</h3>
            <div className="yr-body">
              <div className="yr-2col" style={{ marginBottom: 16 }}>
                <div className="yr-kpi">
                  <div className="yr-kpi-label">Mean time to detect (MTTD)</div>
                  <div className="yr-kpi-value" style={{ fontSize: '1.4em' }}>
                    {(() => {
                      const last = a.slaTrend.filter((s) => s.avgMttdMinutes != null).slice(-1)[0];
                      return last?.avgMttdMinutes != null ? fmtMins(last.avgMttdMinutes) : ' - ';
                    })()}
                  </div>
                  <div style={{ marginTop: 6 }}>
                    {(() => {
                      const d = deltaPill(a.slaImprovement.mttdPct);
                      return (
                        <span className="yr-improvement" style={{ background: d.color + '22', color: d.color }}>
                          {d.text}{a.slaImprovement.mttdFirstMonth && a.slaImprovement.mttdLastMonth
                            ? ` ${monthLabel(a.slaImprovement.mttdFirstMonth)}→${monthLabel(a.slaImprovement.mttdLastMonth)}` : ''}
                        </span>
                      );
                    })()}
                  </div>
                </div>
                <div className="yr-kpi">
                  <div className="yr-kpi-label">Mean time to resolve (MTTR)</div>
                  <div className="yr-kpi-value" style={{ fontSize: '1.4em' }}>
                    {(() => {
                      const last = a.slaTrend.filter((s) => s.avgMttrMinutes != null).slice(-1)[0];
                      return last?.avgMttrMinutes != null ? fmtMins(last.avgMttrMinutes) : ' - ';
                    })()}
                  </div>
                  <div style={{ marginTop: 6 }}>
                    {(() => {
                      const d = deltaPill(a.slaImprovement.mttrPct);
                      return (
                        <span className="yr-improvement" style={{ background: d.color + '22', color: d.color }}>
                          {d.text}{a.slaImprovement.mttrFirstMonth && a.slaImprovement.mttrLastMonth
                            ? ` ${monthLabel(a.slaImprovement.mttrFirstMonth)}→${monthLabel(a.slaImprovement.mttrLastMonth)}` : ''}
                        </span>
                      );
                    })()}
                  </div>
                </div>
              </div>
              <div style={{ height: 240 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={slaChartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} unit=" min" />
                    <RTooltip formatter={(v: number) => v == null ? ' - ' : `${v.toFixed(1)} min`} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line type="monotone" dataKey="mttd" name="MTTD" stroke="#2a5298" strokeWidth={2} connectNulls dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="mttr" name="MTTR" stroke="#dc2626" strokeWidth={2} connectNulls dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <p className="text-xs text-slate-500 mt-2 italic">
                Lower is better. A negative percent change means we resolved incidents faster by year-end than at the start.
              </p>
            </div>
          </div>

          {/* ── Tier 2.B — False-positive ratio (signal-to-noise) ── */}
          <div className="yr-section">
            <h3>Signal-to-noise improvement (false-positive ratio)</h3>
            <div className="yr-body">
              <div style={{ marginBottom: 12 }}>
                {(() => {
                  const d = deltaPill(a.fpImprovement.pct);
                  return (
                    <span className="yr-improvement" style={{ background: d.color + '22', color: d.color, fontSize: '0.95em', padding: '6px 14px' }}>
                      False-positive rate {d.text}
                      {a.fpImprovement.firstMonth && a.fpImprovement.lastMonth
                        ? ` from ${monthLabel(a.fpImprovement.firstMonth)} to ${monthLabel(a.fpImprovement.lastMonth)}` : ''}
                    </span>
                  );
                })()}
              </div>
              <div style={{ height: 220 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={fpChartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} unit="%" />
                    <RTooltip formatter={(v: number) => `${v.toFixed(2)}%`} />
                    <Line type="monotone" dataKey="ratioPct" name="FP rate" stroke="#1e3c72" strokeWidth={2} dot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* ── Tier 2.C — MITRE coverage ── */}
          <div className="yr-section">
            <h3>MITRE ATT&amp;CK coverage</h3>
            <div className="yr-body">
              {a.mitreCoverage.length === 0 ? (
                <p className="text-sm text-slate-500 italic">No MITRE-tagged detections this year.</p>
              ) : (
                <>
                  <p className="text-xs text-slate-500 mb-3">
                    Detections grouped by MITRE ATT&amp;CK tactic. {a.mitreCoverage.length} tactic{a.mitreCoverage.length === 1 ? '' : 's'} observed across {Math.max(...a.mitreCoverage.map((m) => m.monthsActive))} month(s).
                  </p>
                  {(() => {
                    const max = Math.max(...a.mitreCoverage.map((m) => m.count), 1);
                    return a.mitreCoverage.map((m) => (
                      <div key={m.tactic} className="yr-mitre-row">
                        <div style={{ fontWeight: 600, color: '#1e3c72' }}>{m.tactic}</div>
                        <div className="yr-mitre-bar"><div style={{ width: `${(m.count / max) * 100}%` }} /></div>
                        <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{m.count.toLocaleString()}</div>
                      </div>
                    ));
                  })()}
                </>
              )}
            </div>
          </div>

          {/* ── Tier 3.A — Insider threat year-in-review ── */}
          <div className="yr-section">
            <h3>Insider risk year-in-review</h3>
            <div className="yr-body">
              {a.insiderYear.flaggedUserCount === 0 ? (
                <p className="text-sm" style={{ color: '#16a34a', fontWeight: 600 }}>
                  Clean year - no insider-risk anomalies flagged in any month.
                </p>
              ) : (
                <>
                  <p className="text-sm text-slate-700 mb-3">
                    We flagged <strong>{a.insiderYear.flaggedUserCount}</strong> user
                    {a.insiderYear.flaggedUserCount === 1 ? '' : 's'} for anomalous behaviour this year
                    {a.insiderYear.highRiskUserCount > 0 && (
                      <> - <strong style={{ color: '#b91c1c' }}>{a.insiderYear.highRiskUserCount}</strong> at high risk</>
                    )}.
                  </p>
                  <table className="yr-table">
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Risk</th>
                        <th style={{ textAlign: 'right' }}>Peak score</th>
                        <th style={{ textAlign: 'right' }}>Months flagged</th>
                        <th>Top signal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {a.insiderYear.topRiskyUsers.map((u) => (
                        <tr key={u.user}>
                          <td style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>{u.user}</td>
                          <td><span className={`yr-pill ${u.level}`}>{u.level}</span></td>
                          <td style={{ textAlign: 'right', fontWeight: 600 }}>{u.maxScore}</td>
                          <td style={{ textAlign: 'right' }}>{u.monthsFlagged}</td>
                          <td style={{ color: '#475569' }}>{u.topSignal || ' - '}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          </div>

          {/* ── Tier 3.B — Identity attack surface ── */}
          <div className="yr-section">
            <h3>Identity attack surface</h3>
            <div className="yr-body">
              <div className="yr-hero" style={{ background: 'linear-gradient(135deg, #7c2d12 0%, #b91c1c 100%)', marginBottom: 16, padding: '18px 22px' }}>
                <div className="yr-hero-headline">
                  <strong>{fmtInt(a.identitySurface.failedSignInTotal)}</strong> failed sign-in attempts
                  blocked this year, originating from <strong>{a.identitySurface.topCountries.length}</strong> countries.
                </div>
              </div>
              <div className="yr-2col">
                <div>
                  <h4 className="text-sm font-semibold text-slate-800 mb-2">Top source countries</h4>
                  {a.identitySurface.topCountries.length === 0 ? (
                    <p className="text-xs text-slate-500 italic">No country attribution available.</p>
                  ) : (
                    <table className="yr-table">
                      <thead><tr><th>Country</th><th style={{ textAlign: 'right' }}>Count</th></tr></thead>
                      <tbody>
                        {a.identitySurface.topCountries.map((c) => (
                          <tr key={c.country}>
                            <td>{c.country}</td>
                            <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{c.count.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-slate-800 mb-2">Top failure reasons</h4>
                  {a.identitySurface.topFailureReasons.length === 0 ? (
                    <p className="text-xs text-slate-500 italic">No failure-reason data available.</p>
                  ) : (
                    <table className="yr-table">
                      <thead><tr><th>Reason</th><th style={{ textAlign: 'right' }}>Count</th></tr></thead>
                      <tbody>
                        {a.identitySurface.topFailureReasons.map((r) => (
                          <tr key={r.description}>
                            <td style={{ fontSize: 12 }}>{r.description}</td>
                            <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{r.count.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ── Tier 3.C — Threat intel hits ── */}
          <div className="yr-section">
            <h3>Threat intelligence hits</h3>
            <div className="yr-body">
              {a.threatIntelYear.totalMatches === 0 ? (
                <p className="text-sm text-slate-500 italic">No threat-intelligence matches recorded this year.</p>
              ) : (
                <>
                  <p className="text-sm text-slate-700 mb-3">
                    We matched <strong>{fmtInt(a.threatIntelYear.totalMatches)}</strong> threat-intelligence
                    indicators against this customer's environment this year.
                  </p>
                  <div className="yr-2col">
                    <div>
                      <h4 className="text-sm font-semibold text-slate-800 mb-2">By indicator type</h4>
                      <table className="yr-table">
                        <thead><tr><th>Type</th><th style={{ textAlign: 'right' }}>Matches</th></tr></thead>
                        <tbody>
                          {a.threatIntelYear.byType.map((t) => (
                            <tr key={t.indicatorType}>
                              <td>{t.indicatorType}</td>
                              <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{t.count.toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div style={{ height: 220 }}>
                      <h4 className="text-sm font-semibold text-slate-800 mb-2">Matches per month</h4>
                      <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={a.threatIntelYear.byMonth.map((r) => ({ label: monthLabel(r.month), count: r.count }))}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                          <YAxis tick={{ fontSize: 11 }} />
                          <RTooltip />
                          <Bar dataKey="count" fill="#1e3c72" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* ── Tier 3.D — Asset risk concentration ── */}
          <div className="yr-section">
            <h3>Asset risk concentration</h3>
            <div className="yr-body">
              {a.assetRisk.totalEvents === 0 ? (
                <p className="text-sm text-slate-500 italic">No asset-attribution data available.</p>
              ) : (
                <>
                  <p className="text-sm text-slate-700 mb-3">
                    The top 5 assets absorbed{' '}
                    <strong style={{ color: a.assetRisk.top5SharePct > 50 ? '#b91c1c' : '#1e3c72' }}>
                      {fmtPct(a.assetRisk.top5SharePct)}
                    </strong>{' '}
                    of all incident traffic this year - prioritise hardening / patching here for the biggest reduction.
                  </p>
                  <table className="yr-table">
                    <thead>
                      <tr>
                        <th style={{ width: 30 }}>#</th>
                        <th>Asset</th>
                        <th style={{ textAlign: 'right' }}>Incidents</th>
                        <th style={{ textAlign: 'right' }}>Share of total</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {a.assetRisk.topAssets.map((asset, i) => (
                        <tr key={asset.host}>
                          <td style={{ color: '#94a3b8' }}>{i + 1}</td>
                          <td style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>{asset.host}</td>
                          <td style={{ textAlign: 'right', fontWeight: 600 }}>{asset.count.toLocaleString()}</td>
                          <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtPct(asset.sharePct)}</td>
                          <td style={{ width: 120 }}>
                            <div className="yr-mitre-bar"><div style={{ width: `${Math.min(100, asset.sharePct * 2)}%` }} /></div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          </div>

          {/* ── Per-month snapshot table (audit trail) ── */}
          <div className="yr-section">
            <h3>Per-month snapshot audit trail</h3>
            <div className="yr-body" style={{ overflowX: 'auto' }}>
              <table className="yr-table">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th style={{ textAlign: 'right' }}>Total</th>
                    <th style={{ textAlign: 'right' }}>Severity sum</th>
                    <th style={{ textAlign: 'right' }}>High</th>
                    <th>Source</th>
                    <th>Reconciled</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {data.months.map((m) => {
                    const status = !m.hasSnapshot
                      ? (m.isPast ? 'missing' : 'future')
                      : m.source ?? 'manual';
                    return (
                      <tr key={m.month}>
                        <td>{m.month}</td>
                        <td style={{ textAlign: 'right' }}>{m.hasSnapshot ? m.totalIncidents : ' - '}</td>
                        <td style={{ textAlign: 'right' }}>{m.hasSnapshot ? m.severityTotal : ' - '}</td>
                        <td style={{ textAlign: 'right', color: m.highSeverityIncidents > 0 ? '#b91c1c' : '#1f2937' }}>
                          {m.hasSnapshot ? m.highSeverityIncidents : ' - '}
                        </td>
                        <td>
                          <span className={`yr-pill ${status}`}>
                            {!m.hasSnapshot
                              ? (m.isPast ? 'missing' : 'future')
                              : (m.source ?? 'manual')}
                          </span>
                        </td>
                        <td>
                          {m.hasSnapshot
                            ? (m.reconciled === 'yes' ? 'yes' : m.reconciled === 'no' ? 'no' : ' - ')
                            : ' - '}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {m.isPast && !m.hasSnapshot && (
                            <button
                              type="button"
                              onClick={() => { void backfill(m.month); }}
                              disabled={backfilling === m.month}
                              className="inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                            >
                              {backfilling === m.month ? 'Saving…' : 'Backfill'}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>


          {/* 12-Month Overview */}
          {data.twelveMonthOverview && data.twelveMonthOverview.length > 0 && (
            <div style={{background:'#fff',border:'1px solid #e2e8f0',borderRadius:12,padding:'24px 28px',margin:'30px 0 0',boxShadow:'0 2px 8px rgba(0,0,0,0.06)'}}>
              <div style={{color:'#1e3c72',fontWeight:800,fontSize:'1.1em',borderBottom:'2px solid #2a5298',paddingBottom:10,marginBottom:20}}>
                12-Month Overview (Jan {data.year} &#8211; Dec {data.year})
              </div>

              {/* Table */}
              <div style={{overflowX:'auto',marginBottom:24}}>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                  <thead><tr>
                    <th style={{textAlign:'left',padding:'8px 10px',background:'#1e3c72',color:'#fff',fontWeight:600,minWidth:140,whiteSpace:'nowrap'}}>Metric</th>
                    {data.twelveMonthOverview.map((m) => (
                      <th key={m.month} style={{textAlign:'center',padding:'8px 6px',background:'#1e3c72',color:'#fff',fontWeight:600,minWidth:48}}>
                        {monthLabel(m.month)}<br/><span style={{fontWeight:400,fontSize:10,opacity:0.85}}>{m.month.slice(0,4)}</span>
                      </th>
                    ))}
                  </tr></thead>
                  <tbody>
                    <tr style={{background:'#fff'}}>
                      <td style={{padding:'8px 10px',fontWeight:600,color:'#1e3c72',borderBottom:'1px solid #f1f5f9',whiteSpace:'nowrap'}}>No. of Alerts</td>
                      {data.twelveMonthOverview.map((m) => (
                        <td key={m.month} style={{padding:'8px 6px',textAlign:'center',borderBottom:'1px solid #f1f5f9',color:m.hasData?'#1f2937':'#94a3b8',fontWeight:m.hasData?600:400}}>
                          {m.hasData ? m.alerts.toLocaleString() : '0'}
                        </td>
                      ))}
                    </tr>
                    <tr style={{background:'#f8fafc'}}>
                      <td style={{padding:'8px 10px',fontWeight:600,color:'#1e3c72',borderBottom:'1px solid #f1f5f9',whiteSpace:'nowrap'}}>No. of Incidents</td>
                      {data.twelveMonthOverview.map((m) => (
                        <td key={m.month} style={{padding:'8px 6px',textAlign:'center',borderBottom:'1px solid #f1f5f9',color:m.hasData?'#1f2937':'#94a3b8',fontWeight:m.hasData?600:400}}>
                          {m.hasData ? m.incidents.toLocaleString() : '0'}
                        </td>
                      ))}
                    </tr>
                    <tr style={{background:'#fff'}}>
                      <td style={{padding:'8px 10px',fontWeight:600,color:'#1e3c72',whiteSpace:'nowrap'}}>True Positive Rate (%)</td>
                      {data.twelveMonthOverview.map((m) => (
                        <td key={m.month} style={{padding:'8px 6px',textAlign:'center',color:m.hasData&&m.tpr!=null?'#166534':'#94a3b8',fontWeight:m.hasData?600:400}}>
                          {m.hasData && m.tpr != null ? m.tpr.toFixed(1) : '0.0'}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>

              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={twelveMonthChartData} margin={{top:8,right:52,left:16,bottom:8}}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"/>
                  <XAxis dataKey="label" tick={{fontSize:10,fill:'#64748b'}} interval={0} angle={-15} textAnchor="end" height={40}/>
                  <YAxis yAxisId="left" tick={{fontSize:11,fill:'#64748b'}} allowDecimals={false} label={{value:'Alerts',angle:-90,position:'insideLeft',fill:'#64748b',fontSize:11,dy:20}}/>
                  <YAxis yAxisId="right" orientation="right" domain={[0,100]} tick={{fontSize:11,fill:'#16a34a'}} tickFormatter={(v: number) => `${v}%`}/>
                  <RTooltip formatter={(value: number, name: string) => name === 'TPR %' ? [`${value.toFixed(1)}%`, name] : [value.toLocaleString(), name]}/>
                  <Legend wrapperStyle={{fontSize:12}}/>
                  <Line yAxisId="left" type="monotone" dataKey="alerts" name="Alerts" stroke="#2563eb" strokeWidth={2} dot={{r:3}} activeDot={{r:5}} connectNulls/>
                  <Line yAxisId="left" type="monotone" dataKey="incidents" name="Incidents" stroke="#f97316" strokeWidth={2} dot={{r:3}} activeDot={{r:5}} connectNulls/>
                  <Line yAxisId="right" type="monotone" dataKey="tpr" name="TPR %" stroke="#16a34a" strokeWidth={2} dot={{r:3}} activeDot={{r:5}} connectNulls/>
                </LineChart>
              </ResponsiveContainer>
              <div style={{marginTop:20,background:'#fefce8',border:'1px solid #fde68a',borderRadius:8,padding:'10px 14px',fontSize:12,color:'#713f12'}}>
                Monthly observations and operational notes are reflected in the charts and tables above.
                Months with no data yet are shown as zero and will be populated once the corresponding monthly snapshot is available.
              </div>
            </div>
          )}

          <ReportFooter generatedAt={data.generatedAt} timezone={data.timezone} />
        </>
      )}
    </div>
  );
}
